// ============================================
// PortalTrabajos.js — pestaña "Trabajos" del portal del contratista (CONTRATISTAS-EXTERNOS Bloque 3)
// ============================================
// Celular primero (en obra, con una mano). Pinta las ORDENES DE TRABAJO que arma el servidor (sin dinero, sin
// expediente): que trabajo, cuando, el sitio SOLO en la ventana que toca (lo decide el servidor: `visibilidad`), las
// instrucciones de Beep y, persona por persona de SU equipo, la respuesta: Aceptar / Rechazar (motivo obligatorio) /
// Llegue (en curso) / Termine. O mandar a otra persona de su equipo en lugar de la propuesta.

import React, { useCallback, useEffect, useState } from 'react';
import { Briefcase, Clock, MapPin, Phone, Check, X, Play, Flag, Repeat, Info } from 'lucide-react';
import { PP_EXP, COL_EXP as COL } from './components/helpersExpedientes';
import { api, Aviso, card, btnP, btnS, input, lbl } from './Contratistas';

const TIPOS = { levantamiento: 'Levantamiento', instalacion: 'Instalación', servicio: 'Servicio', reparacion: 'Reparación', otro: 'Otro' };
const ESTADO = {
  propuesto: { label: 'Te lo proponen', color: '#9A5B00', bg: '#FFF4D6' },
  aceptado: { label: 'Aceptado', color: '#1E7A4C', bg: '#E6F4EC' },
  rechazado: { label: 'Rechazado', color: '#B42318', bg: '#FEE2E2' },
  sin_respuesta: { label: 'Sin respuesta (venció)', color: '#5E666E', bg: '#EEF0F2' },
  en_curso: { label: 'En curso', color: '#1D9E75', bg: '#E6F4EC' },
  terminado: { label: 'Terminado', color: '#2E7AC4', bg: '#EAF2FB' },
  cerrado: { label: 'Cerrado', color: '#3D4248', bg: '#E5E7EB' },
};
const fmtDia = (iso) => new Date(iso).toLocaleDateString('es-MX', { timeZone: 'America/Mexico_City', weekday: 'long', day: '2-digit', month: 'long' });
const fmtHora = (iso) => new Date(iso).toLocaleTimeString('es-MX', { timeZone: 'America/Mexico_City', hour: '2-digit', minute: '2-digit' });
const ymdMX = (iso) => new Date(iso).toLocaleDateString('en-CA', { timeZone: 'America/Mexico_City' });

function Chip({ estado }) {
  const d = ESTADO[estado] || ESTADO.propuesto;
  return <span style={{ fontSize: 11.5, fontWeight: 700, color: d.color, background: d.bg, borderRadius: 6, padding: '2px 8px', whiteSpace: 'nowrap' }}>{d.label}</span>;
}

export default function PortalTrabajos({ esCelular }) {
  const [r, setR] = useState(null);
  const [error, setError] = useState('');
  const [verPasados, setVerPasados] = useState(false);
  const cargar = useCallback(async () => {
    try { setR(await api('/api/externo/ordenes')); setError(''); }
    catch (e) { setError(e.message || 'No se pudieron cargar tus trabajos.'); setR({ ordenes: [] }); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  if (!r) return <div style={{ color: COL.grisClaro }}>Cargando…</div>;
  const todas = r.ordenes || [];
  const cerradas = (o) => o.cancelada || o.cerrada || o.personas.every(p => ['rechazado', 'sin_respuesta', 'cerrado'].includes(p.estado));
  const vivas = todas.filter(o => !cerradas(o));
  const pasadas = todas.filter(cerradas);
  if (!todas.length) return (
    <div style={{ ...card, textAlign: 'center', padding: 28, color: COL.grisClaro }}>
      <Briefcase size={34} style={{ opacity: 0.4 }} />
      <div style={{ marginTop: 8, fontSize: 14 }}>{r.mensaje || 'Aun no tienes trabajos asignados.'}</div>
      <div style={{ fontSize: 12.5, marginTop: 4 }}>Cuando Beep Mexico te proponga uno, aparecera aqui para que lo aceptes o lo rechaces.</div>
    </div>
  );
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {error && <Aviso>{error}</Aviso>}
      {vivas.length === 0 && <div style={{ ...card, color: COL.grisClaro, fontSize: 13.5 }}>No tienes trabajos pendientes ahora mismo.</div>}
      {vivas.map(o => <Orden key={o.id} orden={o} esCelular={esCelular} onCambio={cargar} />)}
      {pasadas.length > 0 && (
        <button type="button" onClick={() => setVerPasados(v => !v)} style={{ ...btnS, justifyContent: 'center' }}>{verPasados ? 'Ocultar' : 'Ver'} trabajos cerrados ({pasadas.length})</button>
      )}
      {verPasados && pasadas.map(o => <Orden key={o.id} orden={o} esCelular={esCelular} onCambio={cargar} pasada />)}
    </div>
  );
}

function Orden({ orden: o, esCelular, onCambio, pasada }) {
  const [sub, setSub] = useState(null);   // { tipo: 'rechazar' | 'sustituir', persona }
  const [ocupado, setOcupado] = useState(null);
  const [error, setError] = useState('');
  const unDia = ymdMX(o.fecha_inicio) === ymdMX(o.fecha_fin);

  const responder = async (persona, accion, motivo) => {
    setOcupado(persona.id); setError('');
    try { await api(`/api/externo/ordenes/${o.id}/personas/${persona.id}/responder`, { method: 'POST', body: JSON.stringify({ accion, motivo }) }); setSub(null); onCombioSeguro(); }
    catch (e) { setError(e.message || 'No se pudo guardar tu respuesta.'); }
    setOcupado(null);
  };
  const onCombioSeguro = () => { if (typeof onCambio === 'function') onCambio(); };
  const sustituir = async (persona, integranteId) => {
    setOcupado(persona.id); setError('');
    try { await api(`/api/externo/ordenes/${o.id}/personas/${persona.id}/sustituir`, { method: 'POST', body: JSON.stringify({ integrante_id: integranteId }) }); setSub(null); onCombioSeguro(); }
    catch (e) { setError(e.message || 'No se pudo cambiar a la persona.'); }
    setOcupado(null);
  };

  const b = (primario, extra = {}) => ({ ...(primario ? btnP : btnS), minHeight: 44, flex: esCelular ? '1 1 45%' : '0 0 auto', justifyContent: 'center', ...extra });
  return (
    <div style={{ ...card, opacity: pasada ? 0.8 : 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11.5, fontWeight: 700, color: COL.grisClaro, textTransform: 'uppercase', letterSpacing: 0.4 }}>{TIPOS[o.tipo] || o.tipo}</span>
        {o.cancelada && <span style={{ fontSize: 11.5, fontWeight: 700, color: '#B42318', background: '#FEE2E2', borderRadius: 6, padding: '2px 8px' }}>Cancelado por Beep</span>}
        {o.cerrada && !o.cancelada && <span style={{ fontSize: 11.5, fontWeight: 700, color: '#3D4248', background: '#E5E7EB', borderRadius: 6, padding: '2px 8px' }}>Cerrado</span>}
        <span style={{ marginLeft: 'auto', fontSize: 11.5, color: COL.grisClaro }}>Orden #{o.id}</span>
      </div>
      {o.titulo && <div style={{ fontSize: 16, fontWeight: 700, color: COL.oscuro, marginTop: 4 }}>{o.titulo}</div>}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 8, fontSize: 14, color: COL.gris }}>
        <Clock size={17} color={COL.grisClaro} style={{ flexShrink: 0, marginTop: 2 }} />
        <div>
          <div style={{ fontWeight: 600, color: COL.oscuro, textTransform: 'capitalize' }}>{fmtDia(o.fecha_inicio)}{!unDia && <> → {fmtDia(o.fecha_fin)}</>}</div>
          <div style={{ fontSize: 13 }}>{fmtHora(o.fecha_inicio)} a {fmtHora(o.fecha_fin)}{!unDia && ' (varios dias)'}</div>
        </div>
      </div>
      <Sitio o={o} />
      {o.instrucciones && (
        <div style={{ display: 'flex', gap: 8, marginTop: 10, background: '#F8FAFC', borderRadius: 10, padding: '10px 12px', fontSize: 13.5, color: COL.gris, lineHeight: 1.45 }}>
          <Info size={16} color={COL.principal} style={{ flexShrink: 0, marginTop: 2 }} />
          <div><b>Instrucciones de Beep:</b> {o.instrucciones}</div>
        </div>
      )}
      {error && <div style={{ marginTop: 10 }}><Aviso>{error}</Aviso></div>}
      <div style={{ marginTop: 12, borderTop: `1px dashed ${COL.borde}`, paddingTop: 10 }}>
        <div style={{ fontSize: 11.5, fontWeight: 700, color: COL.grisClaro, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>Tu gente en este trabajo</div>
        <div style={{ display: 'grid', gap: 10 }}>
          {o.personas.map(p => {
            const abierta = !o.cancelada && !o.cerrada;
            return (
              <div key={p.id} style={{ background: '#FBFBFC', border: `1px solid ${COL.borde}`, borderRadius: 10, padding: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 14.5, fontWeight: 700, color: COL.oscuro, flex: 1, minWidth: 0 }}>{p.nombre}</span>
                  <Chip estado={p.estado} />
                </div>
                {p.estado === 'rechazado' && p.motivo_rechazo && <div style={{ fontSize: 12.5, color: '#B42318', marginTop: 4 }}>Motivo: {p.motivo_rechazo}</div>}
                {p.estado === 'propuesto' && <div style={{ fontSize: 12.5, color: '#9A5B00', marginTop: 4 }}>Si no contestas en 24 horas, la propuesta vence y el lugar se libera.</div>}
                {abierta && sub && sub.persona.id === p.id && sub.tipo === 'rechazar' && (
                  <Rechazar onCancelar={() => setSub(null)} ocupado={ocupado === p.id} onEnviar={(m) => responder(p, 'rechazar', m)} />
                )}
                {abierta && sub && sub.persona.id === p.id && sub.tipo === 'sustituir' && (
                  <Sustituir persona={p} onCancelar={() => setSub(null)} ocupado={ocupado === p.id} onEnviar={(iid) => sustituir(p, iid)} />
                )}
                {abierta && !(sub && sub.persona.id === p.id) && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                    {p.estado === 'propuesto' && <button type="button" disabled={ocupado === p.id} onClick={() => responder(p, 'aceptar')} style={b(true)}><Check size={16} /> Aceptar</button>}
                    {['propuesto', 'aceptado'].includes(p.estado) && <button type="button" disabled={ocupado === p.id} onClick={() => setSub({ tipo: 'rechazar', persona: p })} style={b(false, { color: '#B42318' })}><X size={16} /> Rechazar</button>}
                    {['propuesto', 'aceptado'].includes(p.estado) && <button type="button" disabled={ocupado === p.id} onClick={() => setSub({ tipo: 'sustituir', persona: p })} style={b(false)}><Repeat size={16} /> Mandar a otra persona</button>}
                    {p.estado === 'aceptado' && <button type="button" disabled={ocupado === p.id} onClick={() => responder(p, 'iniciar')} style={b(true)}><Play size={16} /> Llegué al sitio</button>}
                    {p.estado === 'en_curso' && <button type="button" disabled={ocupado === p.id} onClick={() => responder(p, 'terminar')} style={b(true)}><Flag size={16} /> Terminé</button>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// El sitio lo decide el SERVIDOR: 'minima' (ciudad/region), 'completa' (domicilio y contacto) u 'oculta'.
function Sitio({ o }) {
  if (o.visibilidad === 'oculta' || !o.sitio) {
    return <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 13, color: COL.grisClaro }}><MapPin size={17} /> {o.cerrada || o.cancelada ? 'Sitio ya no visible (trabajo cerrado).' : 'Sitio pendiente.'}</div>;
  }
  const s = o.sitio;
  const zona = [s.ciudad, s.region].filter(Boolean).join(', ');
  if (o.visibilidad === 'minima') {
    return (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 8, fontSize: 13.5, color: COL.gris }}>
        <MapPin size={17} color={COL.grisClaro} style={{ flexShrink: 0, marginTop: 2 }} />
        <div><div style={{ fontWeight: 600, color: COL.oscuro }}>{zona || 'Zona por confirmar'}</div><div style={{ fontSize: 12.5, color: COL.grisClaro }}>El domicilio exacto y el contacto se muestran desde el dia anterior al trabajo.</div></div>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 8, fontSize: 13.5, color: COL.gris }}>
      <MapPin size={17} color={COL.grisClaro} style={{ flexShrink: 0, marginTop: 2 }} />
      <div style={{ minWidth: 0 }}>
        {(s.cliente || s.sucursal) && <div style={{ fontWeight: 600, color: COL.oscuro }}>{[s.cliente, s.sucursal].filter(Boolean).join(' · ')}</div>}
        {s.direccion && <div style={{ overflowWrap: 'anywhere' }}>{s.direccion}</div>}
        {zona && <div style={{ fontSize: 12.5, color: COL.grisClaro }}>{zona}</div>}
        {s.notas_acceso && <div style={{ fontSize: 12.5, marginTop: 2 }}><b>Acceso:</b> {s.notas_acceso}</div>}
        {(s.contacto_nombre || s.contacto_telefono) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
            <Phone size={14} color={COL.grisClaro} />
            <span>{s.contacto_nombre}</span>
            {s.contacto_telefono && <a href={`tel:${s.contacto_telefono}`} style={{ color: COL.principal, fontWeight: 600 }}>{s.contacto_telefono}</a>}
          </div>
        )}
      </div>
    </div>
  );
}

function Rechazar({ onCancelar, onEnviar, ocupado }) {
  const [motivo, setMotivo] = useState('');
  return (
    <div style={{ marginTop: 8 }}>
      <label style={lbl}>¿Por qué no puede ir? (obligatorio)</label>
      <textarea value={motivo} onChange={e => setMotivo(e.target.value)} rows={2} style={{ ...input, resize: 'vertical' }} placeholder="Ej. Ese dia esta en otra obra" />
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <button type="button" disabled={ocupado || motivo.trim().length < 5} onClick={() => onEnviar(motivo)} style={{ ...btnP, background: '#B42318', flex: 1, justifyContent: 'center', opacity: motivo.trim().length < 5 ? 0.6 : 1 }}>Confirmar rechazo</button>
        <button type="button" onClick={onCancelar} style={{ ...btnS, justifyContent: 'center' }}>Volver</button>
      </div>
    </div>
  );
}

function Sustituir({ persona, onCancelar, onEnviar, ocupado }) {
  const [equipo, setEquipo] = useState(null);
  const [sel, setSel] = useState('');
  useEffect(() => { api('/api/externo/integrantes').then(r => setEquipo((r.integrantes || []).filter(i => i.estado === 'activo' && i.id !== persona.integrante_id))).catch(() => setEquipo([])); }, [persona.integrante_id]);
  return (
    <div style={{ marginTop: 8 }}>
      <label style={lbl}>¿Quién va en lugar de {persona.nombre}?</label>
      {equipo === null ? <div style={{ fontSize: 13, color: COL.grisClaro }}>Cargando tu equipo…</div> : (
        <select value={sel} onChange={e => setSel(e.target.value)} style={input}>
          <option value="">Elige a alguien de tu equipo</option>
          {equipo.map(i => <option key={i.id} value={i.id} disabled={!!(i.disponibilidad && i.disponibilidad.disponible === false)}>{i.nombre}{(i.disponibilidad && i.disponibilidad.disponible === false) ? ' — no asignable' : ''}</option>)}
        </select>
      )}
      <div style={{ fontSize: 12, color: COL.grisClaro, marginTop: 4 }}>Solo puede ir quien tenga sus documentos obligatorios vigentes y no tenga otro trabajo a esa hora. Queda aceptado en el acto.</div>
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <button type="button" disabled={ocupado || !sel} onClick={() => onEnviar(Number(sel))} style={{ ...btnP, flex: 1, justifyContent: 'center', opacity: !sel ? 0.6 : 1 }}>Confirmar cambio</button>
        <button type="button" onClick={onCancelar} style={{ ...btnS, justifyContent: 'center' }}>Volver</button>
      </div>
    </div>
  );
}
